import { useCallback, useEffect, useState } from 'react';
import { DevicesApi } from '../../api/resources';
import { errorMessage } from '../../api/client';
import ConfirmModal from '../../components/ConfirmModal';
import Icon from '../../components/Icon';
import { fmtDate } from './useCrmLookups';

const PERMS = [
  ['call_log', 'Call log'],
  ['phone_state', 'Phone state'],
  ['contacts', 'Contacts'],
  ['storage', 'Storage'],
  ['audio', 'Audio'],
  ['notifications', 'Notifications'],
];

export default function DevicesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [modal, setModal] = useState(null); // { mode: 'logout'|'remove', device }

  const load = useCallback(async () => {
    try {
      setRows(await DevicesApi.list());
    } catch (err) {
      setError(errorMessage(err, 'Could not load devices.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const run = async () => {
    const { mode, device } = modal;
    setModal(null);
    setError('');
    try {
      if (mode === 'logout') {
        await DevicesApi.forceLogout(device.id);
        setNotice('The phone will sign out the next time it checks in.');
      } else {
        await DevicesApi.remove(device.id);
        setNotice('Device removed. The staff member can sign in on another phone.');
      }
      load();
    } catch (err) {
      setError(errorMessage(err, 'Action failed.'));
    }
  };

  const ago = (iso) => {
    if (!iso) return 'never';
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 2) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    return fmtDate(iso);
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Devices</h1>
          <p className="page-sub">Phones running the call recorder app.</p>
        </div>
      </div>

      {error && <div className="notice notice-error">{error}</div>}
      {notice && <div className="notice">{notice}</div>}

      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Staff</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Permissions</th>
                <th>Last seen</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                rows.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <div className="name">{d.user?.name || '—'}</div>
                      <div className="sub">{d.user?.username}</div>
                    </td>
                    <td>
                      <div>{d.model || 'Unknown'}</div>
                      <div className="sub">Android {d.os_version || '?'} · app {d.app_version || '?'}</div>
                    </td>
                    <td>
                      {d.battery_percentage != null && (
                        <div>🔋 {d.battery_percentage}%{d.is_charging ? ' ⚡' : ''}</div>
                      )}
                      <div className="sub">
                        {d.connection_type || '—'}
                        {d.current_call_status === 'in_call' && ' · on a call'}
                        {d.should_logout && ' · sign-out pending'}
                      </div>
                    </td>
                    <td>
                      {PERMS.map(([k, label]) => (
                        <span key={k} className={d.permissions[k] === false ? 'badge badge-danger' : 'badge badge-plain'} title={label}>
                          {label}
                          {d.permissions[k] === false ? ' ✕' : ''}
                        </span>
                      ))}
                    </td>
                    <td>{ago(d.last_updated_at)}</td>
                    <td>
                      <div className="row-actions">
                        <button className="btn btn-sm" onClick={() => setModal({ mode: 'logout', device: d })}>
                          Force sign-out
                        </button>
                        <button className="icon-btn danger" title="Remove device" onClick={() => setModal({ mode: 'remove', device: d })}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {!loading && rows.length === 0 && (
          <div className="empty">
            <div className="empty-icon"><Icon name="mobile" size={26} /></div>
            <strong>No devices yet</strong>
            A phone appears here after a staff member signs in to the app.
          </div>
        )}
      </div>

      {modal && (
        <ConfirmModal
          title={modal.mode === 'logout' ? 'Force sign-out' : 'Remove device'}
          message={
            modal.mode === 'logout'
              ? `Sign ${modal.device.user?.name || 'this user'} out of the app on this phone? It happens when the phone next connects.`
              : `Remove this device and revoke its app access? Use this for lost or broken phones.`
          }
          onConfirm={run}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
